<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumbs" />

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary"></b-spinner>
    </div>

    <div v-else>
      <p class="tp-subtitle">
        Imprima uma 2ª etiqueta no credenciamento com as marcas que cada participante deve visitar.
      </p>

      <!-- ===================== CONFIG ===================== -->
      <b-card no-body class="tp-card mb-4">
        <div class="tp-card-head">
          <div class="tp-head-title">
            <span class="tp-icon tp-icon-primary"><i class="bx bx-cog"></i></span>
            <div>
              <h5 class="mb-0">Configuração</h5>
              <span class="tp-muted">Ative e ajuste a etiqueta impressa após a credencial.</span>
            </div>
          </div>
          <div class="custom-control custom-switch pt-1">
            <input type="checkbox" class="custom-control-input" id="hasJourney" v-model="config.has_journey_label">
            <label class="custom-control-label font-weight-medium" for="hasJourney">
              {{ config.has_journey_label ? 'Ativada' : 'Desativada' }}
            </label>
          </div>
        </div>

        <div v-if="config.has_journey_label" class="tp-card-body">
          <div class="row">
            <div class="col-md-4 form-group">
              <label class="tp-label">Tipo de papel</label>
              <select class="form-control" v-model="config.journey_label_config.media">
                <option value="dk22205">62mm contínuo (recomendado)</option>
                <option value="dk11202">62mm × 100mm (die-cut)</option>
                <option value="dk11201">29mm × 90mm (die-cut)</option>
              </select>
            </div>
            <div class="col-md-8 form-group">
              <label class="tp-label">Título da etiqueta</label>
              <input type="text" class="form-control" maxlength="120" v-model="config.journey_label_config.header_text">
              <small class="text-muted">
                O layout é automático: até 3 logos por linha, no máximo 6 na etiqueta,
                todos dimensionados com o mesmo peso visual.
              </small>
            </div>
          </div>
          <div class="d-flex flex-wrap" style="gap: 20px;">
            <div class="custom-control custom-checkbox">
              <input type="checkbox" class="custom-control-input" id="showName" v-model="config.journey_label_config.show_name">
              <label class="custom-control-label" for="showName">Mostrar nome da marca</label>
            </div>
            <div class="custom-control custom-checkbox">
              <input type="checkbox" class="custom-control-input" id="showCode" v-model="config.journey_label_config.show_code">
              <label class="custom-control-label" for="showCode">Mostrar código (Marca 01…)</label>
            </div>
          </div>
        </div>

        <div class="tp-card-foot">
          <b-button variant="primary" :disabled="savingConfig" @click="saveConfig">
            <i class="bx bx-save mr-1"></i>{{ savingConfig ? 'Salvando...' : 'Salvar configuração' }}
          </b-button>
        </div>
      </b-card>

      <!-- ===================== CATÁLOGO ===================== -->
      <b-card no-body class="tp-card">
        <div class="tp-card-head">
          <div class="tp-head-title">
            <span class="tp-icon tp-icon-success"><i class="bx bx-map-alt"></i></span>
            <div>
              <h5 class="mb-0">Catálogo de marcas</h5>
              <span class="tp-muted">{{ marks.length }} marca(s) cadastrada(s)</span>
            </div>
          </div>
          <div>
            <b-button variant="outline-secondary" size="sm" class="mr-2" @click="$refs.importModal.open()">
              <i class="bx bx-upload mr-1"></i> Importar em massa
            </b-button>
            <b-button variant="success" size="sm" @click="openMarkModal()">
              <i class="bx bx-plus mr-1"></i> Adicionar marca
            </b-button>
          </div>
        </div>

        <div class="tp-card-body pt-0">
          <div v-if="!marks.length" class="tp-empty">
            <i class="bx bx-map-alt"></i>
            <p class="mb-1">Nenhuma marca cadastrada ainda.</p>
            <small class="text-muted">Adicione as marcas (estandes/patrocinadores) que os participantes devem visitar.</small>
          </div>

          <div v-else class="table-responsive">
            <table class="table tp-table align-middle mb-0">
              <thead>
                <tr>
                  <th style="width:64px">Logo</th>
                  <th style="width:120px">Código</th>
                  <th>Nome</th>
                  <th style="width:80px" class="text-center">Ordem</th>
                  <th style="width:90px" class="text-center">Status</th>
                  <th style="width:110px" class="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="m in marks" :key="m.id">
                  <td>
                    <div class="tp-logo" :style="{ borderColor: m.color || '#e9e9ef' }">
                      <img v-if="m.logo_url" :src="m.logo_url" alt="">
                      <span v-else class="tp-logo-fallback" :style="{ background: m.color || '#eef1f6' }">
                        {{ (m.name || '?').charAt(0).toUpperCase() }}
                      </span>
                    </div>
                  </td>
                  <td><span class="text-muted">{{ m.code || '—' }}</span></td>
                  <td class="font-weight-medium">{{ m.name }}</td>
                  <td class="text-center">{{ m.order }}</td>
                  <td class="text-center">
                    <span class="badge" :class="m.is_active ? 'badge-soft-success' : 'badge-soft-secondary'">
                      {{ m.is_active ? 'Ativa' : 'Inativa' }}
                    </span>
                  </td>
                  <td class="text-right">
                    <button class="btn btn-sm btn-outline-primary mr-1" @click="openMarkModal(m)"><i class="bx bx-pencil"></i></button>
                    <button class="btn btn-sm btn-outline-danger" @click="deleteMark(m)"><i class="bx bx-trash"></i></button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </b-card>
    </div>

    <!-- Modal criar/editar marca -->
    <b-modal v-model="markModal" :title="markForm.id ? 'Editar marca' : 'Nova marca'" hide-footer>
      <div class="text-center mb-3">
        <label class="tp-logo-upload" @click="$refs.logoInput.click()">
          <img v-if="markForm.logoPreview || currentEditLogo" :src="markForm.logoPreview || currentEditLogo" alt="">
          <span v-else class="tp-logo-upload-empty"><i class="bx bx-image-add"></i><small>Enviar logo</small></span>
        </label>
        <input ref="logoInput" type="file" accept="image/*" class="d-none" @change="onLogoSelected">
      </div>
      <div class="form-group">
        <label class="tp-label">Nome *</label>
        <input type="text" class="form-control" v-model="markForm.name" placeholder="Ex.: Arco">
      </div>
      <div class="row">
        <div class="col-6 form-group">
          <label class="tp-label">Código</label>
          <input type="text" class="form-control" v-model="markForm.code" placeholder="Marca 01">
        </div>
        <div class="col-3 form-group">
          <label class="tp-label">Ordem</label>
          <input type="number" class="form-control" v-model.number="markForm.order">
        </div>
        <div class="col-3 form-group">
          <label class="tp-label">Cor</label>
          <input type="color" class="form-control" style="height: 38px; padding: 3px;" v-model="markForm.color">
        </div>
      </div>
      <div class="custom-control custom-switch mb-3">
        <input type="checkbox" class="custom-control-input" id="markActive" v-model="markForm.is_active">
        <label class="custom-control-label" for="markActive">Ativa</label>
      </div>
      <div class="text-right">
        <b-button variant="light" class="mr-2" @click="markModal = false">Cancelar</b-button>
        <b-button variant="primary" :disabled="savingMark || !markForm.name" @click="saveMark">
          {{ savingMark ? 'Salvando...' : 'Salvar' }}
        </b-button>
      </div>
    </b-modal>

    <ImportModal ref="importModal" :marks="marks" @imported="load" />
  </Layout>
</template>

<script>
import Layout from '@/components/layout/vertical'
import PageHeader from '@/components/page-header'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import services from './services'
import ImportModal from './ImportModal'

export default {
  name: 'trilha-presencial-index',
  components: { Layout, PageHeader, ImportModal },
  page: { title: 'Trilha presencial' },
  data () {
    return {
      title: 'Trilha presencial',
      breadcrumbs: [
        { text: 'Configurações' },
        { text: 'Trilha presencial', active: true }
      ],
      loading: true,
      savingConfig: false,
      config: {
        has_journey_label: false,
        journey_label_config: {
          media: 'dk22205',
          show_name: true,
          show_code: true,
          header_text: 'Trilha presencial'
        }
      },
      marks: [],
      markModal: false,
      savingMark: false,
      markForm: this.emptyMark(),
      currentEditLogo: null
    }
  },
  async mounted () {
    await this.load()
  },
  methods: {
    emptyMark () {
      return { id: null, name: '', code: '', order: 0, color: '#556ee6', is_active: true, logo: undefined, logoPreview: null }
    },
    async load () {
      this.loading = true
      try {
        const cfg = (await services.getConfig()).data
        this.config.has_journey_label = !!cfg.has_journey_label
        if (cfg.journey_label_config) {
          this.config.journey_label_config = Object.assign(this.config.journey_label_config, cfg.journey_label_config)
        }
        const list = (await services.list()).data
        this.marks = (list && list.data) ? list.data : []
      } catch (e) {
        errorMessage('Erro ao carregar a Trilha presencial.')
      } finally {
        this.loading = false
      }
    },
    async saveConfig () {
      this.savingConfig = true
      try {
        await services.updateConfig({
          has_journey_label: this.config.has_journey_label,
          journey_label_config: this.config.journey_label_config
        })
        successMessage('Configuração salva.')
      } catch (e) {
        errorMessage('Erro ao salvar a configuração.')
      } finally {
        this.savingConfig = false
      }
    },
    openMarkModal (mark) {
      if (mark) {
        this.markForm = { id: mark.id, name: mark.name, code: mark.code, order: mark.order, color: mark.color || '#556ee6', is_active: !!mark.is_active, logo: undefined, logoPreview: null }
        this.currentEditLogo = mark.logo_url || null
      } else {
        this.markForm = this.emptyMark()
        this.currentEditLogo = null
      }
      this.markModal = true
    },
    onLogoSelected (e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = () => {
        this.markForm.logo = reader.result
        this.markForm.logoPreview = reader.result
      }
      reader.readAsDataURL(file)
    },
    async saveMark () {
      this.savingMark = true
      try {
        const payload = {
          name: this.markForm.name,
          code: this.markForm.code,
          order: this.markForm.order,
          color: this.markForm.color,
          is_active: this.markForm.is_active
        }
        if (this.markForm.logo !== undefined) payload.logo = this.markForm.logo
        if (this.markForm.id) {
          await services.update(this.markForm.id, payload)
        } else {
          await services.create(payload)
        }
        this.markModal = false
        successMessage('Marca salva.')
        await this.load()
      } catch (e) {
        errorMessage('Erro ao salvar a marca.')
      } finally {
        this.savingMark = false
      }
    },
    async deleteMark (mark) {
      if (!confirm(`Remover a marca "${mark.name}"?`)) return
      try {
        await services.remove(mark.id)
        successMessage('Marca removida.')
        await this.load()
      } catch (e) {
        errorMessage('Erro ao remover a marca.')
      }
    }
  }
}
</script>

<style scoped>
.tp-subtitle { color: #74788d; margin-bottom: 20px; }

.tp-card { border: 1px solid #eff0f2; border-radius: 10px; box-shadow: 0 1px 2px rgba(56, 65, 74, 0.06); }
.tp-card-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 20px; border-bottom: 1px solid #f2f3f5;
}
.tp-head-title { display: flex; align-items: center; gap: 12px; }
.tp-icon {
  width: 42px; height: 42px; border-radius: 10px;
  display: inline-flex; align-items: center; justify-content: center; font-size: 22px;
}
.tp-icon-primary { background: rgba(85, 110, 230, 0.1); color: #556ee6; }
.tp-icon-success { background: rgba(52, 195, 143, 0.1); color: #34c38f; }
.tp-muted { color: #74788d; font-size: 12.5px; }
.tp-label { font-size: 12.5px; font-weight: 600; color: #495057; margin-bottom: 4px; }
.tp-card-body { padding: 18px 20px; }
.tp-card-foot { padding: 14px 20px; border-top: 1px solid #f2f3f5; text-align: right; }

.tp-table thead th {
  border-top: 0; border-bottom: 1px solid #eff0f2;
  font-size: 11px; text-transform: uppercase; letter-spacing: .4px; color: #8a92a6;
}
.tp-table td { border-top: 1px solid #f6f6f9; }

.tp-logo {
  width: 46px; height: 40px; border: 1px solid #e9e9ef; border-radius: 8px;
  overflow: hidden; display: flex; align-items: center; justify-content: center; background: #fff;
}
.tp-logo img { max-width: 100%; max-height: 100%; object-fit: contain; }
.tp-logo-fallback {
  width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;
  color: #fff; font-weight: 700; font-size: 16px;
}

.tp-empty { text-align: center; padding: 40px 20px; color: #74788d; }
.tp-empty i { font-size: 44px; color: #ced4da; display: block; margin-bottom: 10px; }

.tp-logo-upload {
  width: 110px; height: 90px; border: 2px dashed #ced4da; border-radius: 10px;
  display: inline-flex; align-items: center; justify-content: center; cursor: pointer;
  overflow: hidden; background: #fafafa; transition: all .2s;
}
.tp-logo-upload:hover { border-color: #556ee6; }
.tp-logo-upload img { max-width: 100%; max-height: 100%; object-fit: contain; }
.tp-logo-upload-empty { display: flex; flex-direction: column; align-items: center; color: #adb5bd; }
.tp-logo-upload-empty i { font-size: 26px; }
.tp-logo-upload-empty small { font-size: 11px; }
</style>
